<div class="modal-shell">
  <div class="modal-header-row">
    <div>
      <h2 class="modal-title">Create User</h2>
      <p class="modal-subtitle">
        Fill in the details below to create a new account.
      </p>
    </div>
    <button type="button" class="close-btn" (click)="dissmissModal()" aria-label="Close">
      <i class="fa fa-times" aria-hidden="true"></i>
    </button>
  </div>

  <!-- Wizard progress. Phones only — desktop shows the whole form at once. -->
  <div class="su-progress" *ngIf="isStepped">
    <div class="su-progress-meta">
      <span class="su-progress-title">{{ activeStep.title }}</span>
      <span class="su-progress-count">Step {{ currentStep + 1 }} of {{ steps.length }}</span>
    </div>
    <div
      class="su-progress-track"
      role="progressbar"
      aria-valuemin="1"
      [attr.aria-valuenow]="currentStep + 1"
      [attr.aria-valuemax]="steps.length"
      [attr.aria-label]="'Step ' + (currentStep + 1) + ' of ' + steps.length + ': ' + activeStep.title">
      <span
        class="su-progress-seg"
        *ngFor="let step of steps; let i = index"
        [class.is-done]="i <= currentStep"></span>
    </div>
  </div>

  <form [formGroup]="signupForm" (ngSubmit)="onSubmit()" class="modal-form">
    <!-- Step 1 — Account -->
    <div class="su-step" [class.su-active]="currentStep === 0">
      <div class="form-field">
        <label for="username">Username</label>
        <input
          id="username"
          type="text"
          autocomplete="off"
          autocapitalize="none"
          autocorrect="off"
          spellcheck="false"
          enterkeyhint="next"
          formControlName="username"
          (input)="checkUsernameAvailability(signupForm.get('username')?.value || '')"
          class="form-control"
          [class.is-invalid]="signupForm.get('username')?.invalid && signupForm.get('username')?.touched"
          placeholder="Enter username" />
        <div class="error-message" *ngIf="signupForm.get('username')?.invalid && signupForm.get('username')?.touched">
          {{ getErrorMessage('username') }}
        </div>
        <p
          class="availability-text"
          *ngIf="availabilityMessage"
          [ngClass]="{ 'available': isAvailable, 'unavailable': isAvailable === false }">
          {{ availabilityMessage }}
        </p>
        <small class="help-text">Username can contain letters, numbers, symbols, and punctuation characters.</small>
      </div>
    </div>

    <!-- Step 2 — Your name -->
    <div class="su-step" [class.su-active]="currentStep === 1">
      <div class="two-col-grid">
        <div class="form-field">
          <label for="firstName">First Name</label>
          <input
            id="firstName"
            type="text"
            autocomplete="off"
            autocapitalize="words"
            enterkeyhint="next"
            formControlName="firstName"
            class="form-control"
            [class.is-invalid]="signupForm.get('firstName')?.invalid && signupForm.get('firstName')?.touched"
            placeholder="First name" />
          <div class="error-message" *ngIf="signupForm.get('firstName')?.invalid && signupForm.get('firstName')?.touched">
            {{ getErrorMessage('firstName') }}
          </div>
        </div>

        <div class="form-field">
          <label for="lastName">Last Name</label>
          <input
            id="lastName"
            type="text"
            autocomplete="off"
            autocapitalize="words"
            enterkeyhint="next"
            formControlName="lastName"
            class="form-control"
            [class.is-invalid]="signupForm.get('lastName')?.invalid && signupForm.get('lastName')?.touched"
            placeholder="Last name" />
          <div class="error-message" *ngIf="signupForm.get('lastName')?.invalid && signupForm.get('lastName')?.touched">
            {{ getErrorMessage('lastName') }}
          </div>
        </div>
      </div>
    </div>

    <!-- Step 3 — Contact -->
    <div class="su-step" [class.su-active]="currentStep === 2">
      <div class="form-field">
        <label for="email">Email Address</label>
        <input
          id="email"
          type="email"
          autocomplete="off"
          autocapitalize="none"
          autocorrect="off"
          spellcheck="false"
          inputmode="email"
          enterkeyhint="next"
          formControlName="email"
          class="form-control"
          [class.is-invalid]="signupForm.get('email')?.invalid && signupForm.get('email')?.touched"
          placeholder="name@example.com" />
        <div class="error-message" *ngIf="signupForm.get('email')?.invalid && signupForm.get('email')?.touched">
          {{ getErrorMessage('email') }}
        </div>
      </div>

      <div class="form-field">
        <label for="phone_number">Phone Number</label>
        <input
          id="phone_number"
          type="tel"
          autocomplete="off"
          formControlName="phone_number"
          maxlength="10"
          inputmode="numeric"
          enterkeyhint="next"
          (input)="onPhoneInput($event)"
          class="form-control"
          [class.is-invalid]="signupForm.get('phone_number')?.invalid && signupForm.get('phone_number')?.touched"
          placeholder="9876543210" />
        <div class="error-message" *ngIf="signupForm.get('phone_number')?.invalid && signupForm.get('phone_number')?.touched">
          {{ getErrorMessage('phone_number') }}
        </div>
      </div>
    </div>

    <!-- Step 4 — Security -->
    <div class="su-step" [class.su-active]="currentStep === 3">
      <div class="two-col-grid">
        <div class="form-field">
          <label for="pass">Password</label>
          <div class="password-input">
            <input
              id="pass"
              [type]="hidePassword ? 'password' : 'text'"
              autocomplete="new-password"
              enterkeyhint="next"
              formControlName="pass"
              class="form-control"
              [class.is-invalid]="signupForm.get('pass')?.invalid && signupForm.get('pass')?.touched"
              placeholder="Enter password" />
            <button type="button" class="icon-btn" (click)="hidePassword = !hidePassword" aria-label="Toggle password visibility">
              <span class="material-icons">{{ hidePassword ? "visibility_off" : "visibility" }}</span>
            </button>
          </div>
          <div class="error-message" *ngIf="signupForm.get('pass')?.invalid && signupForm.get('pass')?.touched">
            {{ getErrorMessage('pass') }}
          </div>
        </div>

        <div class="form-field">
          <label for="confirmPassword">Confirm Password</label>
          <div class="password-input">
            <input
              id="confirmPassword"
              [type]="hideConfirmPassword ? 'password' : 'text'"
              autocomplete="new-password"
              enterkeyhint="done"
              formControlName="confirmPassword"
              class="form-control"
              [class.is-invalid]="signupForm.get('confirmPassword')?.invalid && signupForm.get('confirmPassword')?.touched"
              placeholder="Re-enter password" />
            <button type="button" class="icon-btn" (click)="hideConfirmPassword = !hideConfirmPassword" aria-label="Toggle confirm password visibility">
              <span class="material-icons">{{ hideConfirmPassword ? "visibility_off" : "visibility" }}</span>
            </button>
          </div>
          <div
            class="error-message"
            *ngIf="(signupForm.get('confirmPassword')?.invalid || signupForm.hasError('mismatch')) && signupForm.get('confirmPassword')?.touched">
            {{ getErrorMessage('confirmPassword') }}
          </div>
        </div>
      </div>

      <div class="form-group terms-consent">
        <label class="terms-consent__label">
          <input type="checkbox" formControlName="acceptTerms" />
          <span>
            I agree to the
            <a href="/terms" target="_blank" rel="noopener">Terms &amp; Conditions</a>
            and
            <a href="/privacy" target="_blank" rel="noopener">Privacy Policy</a>.
          </span>
        </label>
        <div
          class="error-message"
          *ngIf="signupForm.get('acceptTerms')?.invalid && signupForm.get('acceptTerms')?.touched">
          You must accept the Terms &amp; Conditions and Privacy Policy to continue.
        </div>
      </div>
    </div>

    <!-- Desktop actions — unchanged -->
    <div class="action-row" *ngIf="!isStepped">
      <button type="button" class="btn-secondary" (click)="dissmissModal()">
        Cancel
      </button>
      <button type="submit" class="btn-primary" [disabled]="signupForm.invalid || isAvailable === false">
        Create User
      </button>
    </div>

    <!-- Wizard navigation — phones only -->
    <div class="su-nav" *ngIf="isStepped">
      <button
        *ngIf="!isLastStep"
        type="button"
        class="btn-primary"
        (click)="next()"
        [disabled]="!canAdvance">
        Continue
      </button>

      <button
        *ngIf="isLastStep"
        type="submit"
        class="btn-primary"
        [disabled]="signupForm.invalid || isAvailable === false">
        Create User
      </button>

      <!-- Renders only for a step declared optional. No step is today: every
           control on this form is Validators.required. -->
      <button
        *ngIf="activeStep.optional && !isLastStep"
        type="button"
        class="su-skip"
        (click)="skip()">
        Skip this step
      </button>

      <button
        type="button"
        class="btn-secondary"
        (click)="currentStep === 0 ? dissmissModal() : back()">
        {{ currentStep === 0 ? "Cancel" : "Back" }}
      </button>
    </div>
  </form>
</div>
